import dayjs from 'dayjs';
import { camelCase, lowerCase, startCase } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { TASK_CONST } from '@/constant';
import { TASK_FRAME_WORK_STATE_TRANSLATION_KEY_MAPPER } from '@/constant/taskConstant';
import { useFrameSubmitQuery, useTypedSearchParams } from '@/hook';
import type { VStatusBadgeProps, VTableColumnFilter } from '@/package';
import { Send, VIconButton, VStatusBadge, VTable } from '@/package';
import { useAuthStore } from '@/store';
import type { FrameData, FrameSearchParams, Task } from '@/type';
import { getFrameState } from '@/util/frame';

const MyWorkFrameTable = ({
  frameList,
  task,
}: {
  frameList?: FrameData[];
  task?: Task;
}) => {
  const { mutate: submitFrame } = useFrameSubmitQuery();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { getAll, append, remove, flush, get } =
    useTypedSearchParams<FrameSearchParams>();
  const { t } = useTranslation();

  const onRowClick = (frame: FrameData) => {
    navigate(
      `/labeling/${frame.projectID}/${frame.taskID}/${frame.frameSeq}?myWork=true`,
    );
  };

  const onSubmit = (frame: FrameData) => {
    if (frame.workState !== TASK_CONST.TASK_FRAME_WORKSTATE.WORKING) return;
    submitFrame({
      pTaskID: frame.taskID,
      pFrameSeq: frame.frameSeq,
    });
  };

  const taskUserList = task?.userTask?.map(ut => ut.user) ?? [];

  const statusColumnFilterItemList = Object.values(
    TASK_CONST.TASK_FRAME_WORKSTATE,
  ).map(status => ({
    value: status,
    label: t(
      TASK_FRAME_WORK_STATE_TRANSLATION_KEY_MAPPER[
        lowerCase(
          status,
        ) as keyof typeof TASK_FRAME_WORK_STATE_TRANSLATION_KEY_MAPPER
      ],
    ),
    isSelected: getAll('frameWorkState')?.includes(status) ?? false,
  }));

  const reviewerColumnFilterItemList = taskUserList.map(user => ({
    value: user?.id,
    label: user?.name || user?.email.split('@').at(0) || '-',
    isSelected:
      (user?.id && getAll('frameReviewerId')?.includes(user.id)) ?? false,
  }));

  const columnFilter = [
    {
      id: 'status',
      itemList: statusColumnFilterItemList,
    },

    {
      id: 'reviewer',
      itemList: reviewerColumnFilterItemList,
    },
  ];

  const renderedFrameList = useMemo(() => {
    return (
      frameList?.map(frame => {
        const { frameSeq } = frame;
        const totalObjects =
          frame.statistic?.reduce(
            (acc, curr) => acc + (curr.aiCountValue + curr.humanCountValue),
            0,
          ) ?? 0;
        return {
          id: `${frameSeq}`,
          status: getFrameState(frame, task),
          totalObjects: totalObjects,
          reviewer: frame.reviewer?.email.split('@').at(0) ?? '-',
          startDate: frame.workStartTime
            ? dayjs(frame.workStartTime).format('YYYY-MM-DD')
            : '-',
          completionDate: frame.workEndTime
            ? dayjs(frame.workEndTime).format('YYYY-MM-DD')
            : '-',
          submit: null,

          frame,
          task,
        };
      }) ?? []
    );
  }, [frameList, task]);

  return (
    <VTable
      data={renderedFrameList}
      className="flex-1 overflow-auto"
      onRowClick={e => onRowClick(e._target.row.frame)}
      columnList={[
        'id',
        'status',
        'totalObjects',
        'reviewer',
        'startDate',
        'completionDate',
        'submit',
      ]}
      columnStyleMap={{
        id: { className: 'w-[64px]' },
        status: { className: 'w-[104px]' },
        totalObjects: { className: 'w-[244px]' },
        reviewer: { className: 'w-[244px]' },
        startDate: { className: 'w-[124px]' },
        completionDate: { className: 'w-[124px]' },
        submit: { className: 'w-[68px]' },
      }}
      columnRenderer={{
        id: () => 'No.',
        status: startCase,
        totalObjects: startCase,
        reviewer: startCase,
        startDate: startCase,
        completionDate: startCase,
        submit: startCase,
      }}
      cellRenderer={{
        id: value => value,
        status: value => (
          <VStatusBadge
            variant={
              camelCase(value.toLowerCase()) as VStatusBadgeProps['variant']
            }
            size="sm"
          >
            {value}
          </VStatusBadge>
        ),
        totalObjects: value => value,
        reviewer: value => value,
        startDate: value => value,
        completionDate: value => value,
        submit: (_1, _2, row) => {
          const isSubmitEnable =
            row.frame.workState === TASK_CONST.TASK_FRAME_WORKSTATE.WORKING &&
            row.frame.workerID === user.id;

          return (
            <VIconButton
              onClick={e => {
                e.stopPropagation();
                onSubmit(row.frame);
              }}
              disabled={!isSubmitEnable}
              Icon={Send}
            />
          );
        },
      }}
      columnFilter={
        columnFilter as VTableColumnFilter<(typeof renderedFrameList)[number]>[]
      }
      onColumnFilterChange={e => {
        const statusColumnFilter = e.find(filter => filter.id === 'status');
        const reviewerColumnFilter = e.find(filter => filter.id === 'reviewer');

        remove('frameWorkState');
        remove('frameReviewerId');

        statusColumnFilter?.itemList
          .filter(item => item.isSelected)
          .forEach(item => {
            append('frameWorkState', item.value);
          });

        reviewerColumnFilter?.itemList
          .filter(item => item.isSelected)
          .forEach(item => {
            append('frameReviewerId', item.value);
          });

        flush();
      }}
      columnSorter={[
        {
          id: 'id',
          desc: get('frameOrderBy') === '-frameSeq' ? true : false,
        },
      ]}
      onColumnSorterChange={e => {
        remove('frameOrderBy');
        const idSorter = e.find(sorter => sorter.id === 'id');
        idSorter?.desc
          ? append('frameOrderBy', '-frameSeq')
          : append('frameOrderBy', '+frameSeq');
        flush();
      }}
    />
  );
};

export default MyWorkFrameTable;
